import jobsService from '../services/jobs'
import BatchPollingManager from './batchPollingManager'

// One shared timer for all polling instances: collects active ids, sends one
// batched request per cycle, dispatches each id's slice back to its instance.
// Backs off on 429 (honoring Retry-After), resets on success.
class PollingManager extends BatchPollingManager {
  constructor() {
    super()
    // vpId -> polling instance
    this.instances = new Map()
  }

  register(instance) {
    this.instances.set(instance.vpId, instance)
    this._ensureTimer()

    // Poll a running row promptly; concurrent mounts coalesce into one tick.
    this.scheduleImmediate()
  }

  unregister(vpId) {
    this.instances.delete(vpId)
    if (this.instances.size === 0) {
      this._stopTimer()
    }
  }

  // Force a vp into the next batch even before Redux reflects its active status.
  requestImmediatePoll(vpId) {
    const instance = this.instances.get(vpId)
    if (instance) {
      instance.forcePoll = true
    }

    this.scheduleImmediate()
  }

  // Keep the timer alive while any row is mounted, even if none is currently
  // active, so a row that later becomes active is picked up on the next tick.
  _hasWork() {
    return this.instances.size > 0
  }

  _collectIds() {
    const ids = []
    this.instances.forEach(instance => {
      if (instance.forcePoll || instance.shouldPoll()) {
        ids.push(instance.vpId)
      }
    })

    return ids
  }

  _fetch(chunk) {
    return jobsService.progressBatch(chunk)
  }

  async _handleData(data, ids) {
    // Hand each vp its slice; iterate the requested ids so a row unmounted
    // mid-request is simply skipped.
    await Promise.all(
      ids.map(async vpId => {
        const instance = this.instances.get(vpId)
        if (!instance) {
          return
        }

        instance.forcePoll = false
        await instance.processData((data && data[vpId]) || {})
      })
    )
  }

  _handleError(error, ids) {
    // A 429 is transient rate-limiting: back off and retry silently, keeping the
    // rows' current status. Only real errors flip rows to CONNECTIONFAILED.
    const status = error && error.response && error.response.status
    if (status === 429) {
      return
    }

    ids.forEach(vpId => {
      const instance = this.instances.get(vpId)
      if (instance) {
        instance.handleError()
      }
    })
  }
}

export default new PollingManager()
